import { track } from '@eventbrite/datalayer-library';
import { FormattedEvent, isPromoCodeActive } from '@eventbrite/event-renderer';
import {
    EventInteraction,
    EventInteractionContext,
    usePlacementEventContextAggregator,
} from '@eventbrite/event-tracker';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import { logEvent } from '@eventbrite/statsig';
import { $FixMe } from '@eventbrite/ts-utils';
import loadable from '@loadable/component';
import classNames from 'classnames';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import {
    BEST_OF_CITY_EVENTS,
    BUCKET_TYPE_PROFILE,
    POPULAR_EVENTS,
    PRIMARY_BUCKET,
    SECONDARY_BUCKET,
} from '../../../../constants/constants';
import { ALL_TAB, FOR_YOU, FREE_TAB } from '../../../../constants/tabConfig';
import { GetEnrollmentStatus, useHasEnteredViewport } from '../../../../hooks';
import { TrackingContext } from '../../../../trackingContext';
import {
    BucketConfig,
    ConnectedProps,
    DiscoverState,
    Event,
    Events,
    Location,
    OrganizerProfiles,
    SearchCriteriaShape,
} from '../../../../types/index';
import { formatBucketLabelFromKey } from '../../../../utils/buckets';
import { getFormattedTagWithType } from '../../../../utils/categories';
import { getEventFromId } from '../../../../utils/general';
import {
    HEAP_BUCKET_VIEW,
    HEAP_SEE_MORE_BTN_CLICK,
} from '../../constants/analytics';
import {
    clickOnOnboardingCard,
    clickOnSeeMore,
    clickOnViewEvent,
} from '../../redux/events';
import { fetchEventsForTab } from '../../redux/events/';
import { getBucketConfig, getEventsLayout } from '../../utils';
import { removeEvent } from './api';
import {
    DESKTOP_DEFAULT_LAYOUT_CONFIG,
    LAYOUT_WIDTH_MAP,
    MOBILE_DEFAULT_LAYOUT_CONFIG,
    ONLINE_EVENTS,
} from './BucketContainer.constants';
import { useWindowWidth } from './BucketContainer.hooks';
import './BucketPremium.scss';

const BUCKET_KEYS = {
    PRIMARY_BUCKET: 'primary_bucket',
    USER_INTERESTS: 'user_interests_inperson_events',
};

const SeeMoreOfBucket = loadable(() => import('./SeeMoreOfBucket'));
const EmptyState = loadable(() => import('../../components/EmptyState'), {
    resolveComponent: (components: { EmptyState: React.ReactElement }) =>
        components.EmptyState,
});
const ConsumerCards = loadable(() => import('../../components/ConsumerCards'), {
    resolveComponent: (components: { ConsumerCards: React.ReactElement }) =>
        components.ConsumerCards,
});
const OrganizerCards = loadable(
    () => import('../../components/OrganizerCards'),
    {
        resolveComponent: (components: {
            OrganizerCards: React.ReactElement;
        }) => components.OrganizerCards,
    },
);

const _isInOddLayout = (windowWidth: number) =>
    windowWidth >= LAYOUT_WIDTH_MAP.odd && windowWidth < LAYOUT_WIDTH_MAP.even;
interface BucketHeaderProps {
    currentPlace?: string;
    isPrimaryBucket?: boolean;
    isSpecialBucket?: boolean;
    name?:
        | {
              lazyIncludeLocation?: (
                  locationName: string,
              ) => string | GenericLazyString;
          }
        | GenericLazyString;
    subtitle?: {
        lazyIncludeLocation?: (
            locatonName: string,
        ) => string | GenericLazyString;
    };
    icon?: React.ReactNode;
}

const BucketHeader = (props: BucketHeaderProps) => {
    if (!props.name && !props.subtitle) {
        return null;
    }

    let titleComponent = null;
    let subtitleComponent = null;
    let parsedName: any = null;
    if (props.name) {
        parsedName = props.name;

        const titleClassNames = classNames(
            'eds-text-color--grey-900',
            'eds-text-hs',
        );

        if (
            Object.prototype.hasOwnProperty.call(
                props.name,
                'lazyIncludeLocation',
            ) &&
            'lazyIncludeLocation' in props.name
        ) {
            parsedName = props.name.lazyIncludeLocation?.(
                props.currentPlace || '',
            );
        }

        if (props.currentPlace === ONLINE_EVENTS && props.isPrimaryBucket) {
            parsedName = gettext('Online Events');
        }

        titleComponent = <h3 className={titleClassNames}>{parsedName}</h3>;
    }

    if (props.subtitle && props.subtitle.lazyIncludeLocation) {
        const parsedSubtitle = props.subtitle.lazyIncludeLocation(
            props.currentPlace || '',
        );

        subtitleComponent = (
            <span className="eds-text-bm eds-text-color--grey-600">
                {parsedSubtitle}
            </span>
        );
    }

    const bucketHeaderClassNames = classNames(
        'feed__bucket-header',
        {
            'pad-bot-sm-up--premium': props.isSpecialBucket,
            'has-icon': props.icon,
        },
        'eds-l-mn-pad-hor-3',
        'eds-l-md-pad-hor-3',
        'eds-l-mw-pad-hor-3',
        'eds-l-ln-pad-hor-3',
        'eds-l-lg-pad-hor-3',
        'eds-l-lw-pad-hor-3',
    );

    let bucketHeader = null;

    if (subtitleComponent || titleComponent) {
        bucketHeader = (
            <div
                className={bucketHeaderClassNames}
                data-testid="feed-bucket-header"
            >
                {props.icon || null}
                {titleComponent}
                <div>{subtitleComponent}</div>
            </div>
        );
    }

    return bucketHeader;
};

const HOMEPAGE_PLACEMENT_NAME = 'homepage';
const HOMEPAGE_SUB_INTERFACES: { [key: string]: string } = {
    [PRIMARY_BUCKET]: 'events_in',
    [SECONDARY_BUCKET]: 'more_events',
};

function aggregateContext({
    id,
    event: eventData,
    events = { promoted: [], organic: [] },
    eventSearch,
    location,
    user,
    bucketType,
    bucketIndex,
    locale,
    tabKey,
}: Omit<EventInteraction, 'action'> & {
    events: { promoted: Events; organic: Events };
    eventSearch: SearchCriteriaShape;
    location?: Location;
    user: { id?: string; guestId: string };
    bucketType?: string;
    bucketIndex: number;
    locale?: string;
    tabKey?: string;
}): EventInteractionContext {
    const { event, absolutePosition } = getEventFromId(
        events,
        id,
        eventData.isPromoted,
    );
    const openDiscount = (event as FormattedEvent).openDiscount;
    const hasOpenPromotion = isPromoCodeActive(openDiscount);

    return {
        user: {
            id: user.id,
            locale: locale,
            guestId: user.guestId,
            sessionId: window?.__SERVER_DATA__?.request?.session_id,
        },
        adId: event?.promotedListingMetadata?.adId,
        place: {
            name: HOMEPAGE_PLACEMENT_NAME,
            position: absolutePosition,
            page: 1,
            context: {
                date_filter: Array.isArray(eventSearch.dates)
                    ? eventSearch.dates
                    : [eventSearch.dates],
                tags: getFormattedTagWithType(eventSearch.tags),
                place_id: location?.placeId,
                is_online: location?.isOnline,
                latitude: null,
                longitude: null,
            },
            tabKey: tabKey,
            subInterface: {
                name:
                    bucketType && HOMEPAGE_SUB_INTERFACES[bucketType]
                        ? HOMEPAGE_SUB_INTERFACES[bucketType]
                        : bucketType,
                position: `${bucketIndex + 1}`,
            },
            placementId: event?.promotedListingMetadata?.placementId,
        },
        displayContext: { hasOpenPromotion },
        venueId: event?.venue?.id,
    };
}

type OwnProps = {
    events?: Events;
    profiles?: OrganizerProfiles;
    bucketConfig?: BucketConfig;
    bucketKey?: string;
    conditionalRender?: boolean;
    name: $FixMe;
    seeMore?: { url?: string };
    shouldShowSeeMore?: boolean;
    subtitle?: {
        lazyIncludeLocation?: (
            locationName: string,
        ) => string | GenericLazyString;
    };
    type?: string;
    icon?: React.ReactNode;
    isGDPRCountry?: boolean;
    fetchUserSavedEvents: Function;
    gaCategory?: string;
    props?: any;
    enableInterestsSelector?: boolean;
    bucketIndex: number;
    lastBucket: boolean;
};
export const Bucket = ({
    events = [],
    profiles = [],
    searchCriteria = {},
    bucketConfig,
    bucketIndex,
    ...props
}: BucketProps) => {
    GetEnrollmentStatus;
    const [animateIn, setAnimateIn] = useState(false);
    const ref = useRef(null);
    const { hasEnteredViewport: isInViewport } = useHasEnteredViewport(ref, {
        visibleInitial: false,
        rootMargin: '0px 0px 10px 0px',
    });

    const { locale } = useContext(TrackingContext);
    const promotedEvents = (events || []).filter(
        (event: Event) => event?.promotedListingMetadata,
    );
    const organicEvents = (events || []).filter(
        (event: Event) => !event?.promotedListingMetadata,
    );

    usePlacementEventContextAggregator(ref, ({ id, event }) =>
        aggregateContext({
            id,
            event,
            events: { promoted: promotedEvents, organic: organicEvents },
            eventSearch: searchCriteria,
            location: props.location,
            user: { id: props.userId, guestId: props.guestId },
            bucketType: props.bucketKey,
            bucketIndex,
            locale: locale,
            tabKey: props.tabKey,
        }),
    );

    useEffect(() => {
        if (isInViewport && !animateIn) {
            setAnimateIn(true);
        }
    }, [isInViewport, animateIn]);

    const windowWidth = useWindowWidth();
    const isMobile = (windowWidth || 0) <= 660;

    useEffect(() => {
        const shouldTrackBucket =
            props.tabKey === FOR_YOU && props.isAuthenticated && isInViewport;

        if (shouldTrackBucket) {
            let bucketLabel = props.name?.toString();

            if (props.name?.lazyIncludeLocation) {
                bucketLabel = props.name
                    .lazyIncludeLocation?.(props.currentPlace || '')
                    .toString();
            }

            if (bucketLabel) {
                track({
                    eventName: HEAP_BUCKET_VIEW,
                    eventData: {
                        personalizationBucketName: bucketLabel,
                        personalizationBucketKey: props.bucketKey,
                    },
                });
            }
        }
    }, [
        isInViewport,
        props.name,
        props.tabKey,
        props.currentPlace,
        props.bucketKey,
        props.isAuthenticated,
    ]);

    const bucketClassNames = React.useMemo(() => {
        const bucketClassNamesDesktop = classNames(
            'feed-events-bucket',
            `feed-events--${props.bucketKey}`,
            {
                'feed-events-bucket--animated': isInViewport,
                'feed-events-bucket--animate': animateIn,
            },
        );
        const bucketClassNamesMobile = classNames(
            bucketClassNamesDesktop,
            'feed-events-bucket--mobile',
        );

        return isMobile ? bucketClassNamesMobile : bucketClassNamesDesktop;
    }, [isMobile, animateIn, props.bucketKey, isInViewport]);

    const layoutConfig = React.useMemo(() => {
        const overwrite = isMobile
            ? 'overwrite.props.layout.mobile.events'
            : 'overwrite.props.layout.desktop.events';
        const defaultConfig = isMobile
            ? MOBILE_DEFAULT_LAYOUT_CONFIG
            : DESKTOP_DEFAULT_LAYOUT_CONFIG;

        return getBucketConfig({
            bucketConfig: get(bucketConfig, overwrite, {}),
            defaultConfig,
        });
    }, [isMobile, bucketConfig]);

    const eventGroups = React.useMemo(() => {
        if (isMobile) {
            return getEventsLayout({
                events,
                bucketConfig: layoutConfig,
            });
        }

        return getEventsLayout({
            events,
            bucketConfig: layoutConfig,
        });
    }, [isMobile, layoutConfig, events]);

    const enableInterestsSelector = React.useMemo(() => {
        return (
            (!props.isAuthenticated &&
                props.bucketKey === BUCKET_KEYS.PRIMARY_BUCKET) ||
            props.bucketKey === BUCKET_KEYS.USER_INTERESTS
        );
    }, [props.isAuthenticated, props.bucketKey]);

    if (isEmpty(events) && props.tabKey !== FOR_YOU) {
        return (
            <EmptyState
                bucketKey={props.bucketKey}
                emptyState={bucketConfig?.emptyState}
                isAuthenticated={props.isAuthenticated}
            />
        );
    }

    const headerTitle = (
        <BucketHeader
            name={props.name}
            subtitle={props.subtitle}
            currentPlace={props.currentPlace}
            isPrimaryBucket={props.bucketKey === PRIMARY_BUCKET}
            isSpecialBucket={[BEST_OF_CITY_EVENTS, POPULAR_EVENTS].includes(
                props.bucketKey || '',
            )}
            icon={props.icon}
        />
    );
    const formattedBucketLabel = formatBucketLabelFromKey({ ...props });

    return (
        <section ref={ref}>
            {isInViewport ? (
                <div
                    className={bucketClassNames}
                    style={{ minHeight: '100px' }}
                >
                    <ConsumerCards
                        headerTitle={headerTitle}
                        bucketKey={props.bucketKey}
                        bucketLabel={formattedBucketLabel}
                        isAuthenticated={props.isAuthenticated}
                        isFreeTab={searchCriteria.price === FREE_TAB}
                        isFirstBucket={props.tabKey !== ALL_TAB}
                        isInOddLayout={_isInOddLayout(windowWidth || 0)}
                        onClickOnBoardingCard={props.onClickOnBoardingCard}
                        onViewEvent={props.onViewEvent}
                        shouldShowOnboardingCard={get(
                            bucketConfig,
                            'shouldShowOnboardingCard',
                            false,
                        )}
                        bucketLayoutConfig={layoutConfig}
                        eventsGroups={eventGroups}
                        cardType="grid"
                        enableInterestsSelector={enableInterestsSelector}
                        isMobile={isMobile}
                        shareOptions={props.shareOptions}
                        onRemoveEvent={removeEvent}
                        enableEventInterestsControls={props.tabKey === FOR_YOU}
                        tabKey={props.tabKey}
                        locale={locale}
                    />

                    {props.type === BUCKET_TYPE_PROFILE && (
                        <OrganizerCards
                            bucketKey={props.bucketKey}
                            profiles={profiles}
                            isAuthenticated={props.isAuthenticated || false}
                            userId={props.userId}
                            isGDPRCountry={props.isGDPRCountry || false}
                            gaSettings={props.gaSettings}
                        />
                    )}
                    {props.shouldShowSeeMore && (
                        <SeeMoreOfBucket
                            bucketKey={props.bucketKey}
                            seeMore={props.seeMore}
                            onSeeMore={(e: React.MouseEvent<HTMLElement>) => {
                                logEvent(HEAP_SEE_MORE_BTN_CLICK);
                                props.onSeeMore?.(e);
                            }}
                            searchCriteria={searchCriteria}
                            shouldShowSeeMore={true}
                            locationSlug={props.locationSlug}
                            tabKey={props.tabKey}
                            userId={props.userId}
                            seasonalContent={props.seasonalContent}
                        />
                    )}
                </div>
            ) : (
                <div className={bucketClassNames} style={{ height: '100px' }} />
            )}
        </section>
    );
};

const _mapStateToProps = (state: DiscoverState) => ({
    isMobile: state.app.isMobile,
    isAuthenticated: state.user.isAuthenticated,
    searchCriteria: state.content?.flatBucket
        ? state.content.flatBucket.event_search
            ? state.content.flatBucket.event_search
            : state.content.flatBucket.eventSearch
        : { dates: ['current_future'] },
    locationSlug: state.location?.slug,
    currentPlace: state.location?.currentPlace,
    tabKey: state.content?.browseState?.tabKey,
    userId: state.user.publicId,
    seasonalContent: state.content?.seasonal,
    gaSettings: state.gaSettings,
    isGDPRCountry: state.app.isGDPRCountry,
    featureFlags: state.app.featureFlags,
    location: state.location,
    guestId: state.user.guestId,
    shareOptions: state.shareOptions,
});

const _mapDispatchToProps = {
    onSeeMore: clickOnSeeMore,
    onViewEvent: clickOnViewEvent,
    onClickOnBoardingCard: clickOnOnboardingCard,
    fetchEventsForTab,
};

type ReduxProps = ConnectedProps<
    typeof _mapStateToProps,
    typeof _mapDispatchToProps
>;
export type BucketProps = ReduxProps & OwnProps;

export default connect(_mapStateToProps, _mapDispatchToProps)(Bucket);
